import React, { useState } from 'react';
import { DataTable } from './components/DataTable';
import { smartlinks } from './data/mockData';
import { SmartlinkForm } from './forms/SmartlinkForm';
import { SmartlinkDetail } from './detail/SmartlinkDetail';
import { Plus, Copy, Archive, ExternalLink, Eye } from 'lucide-react';
export const Smartlinks: React.FC = () => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [selectedSmartlink, setSelectedSmartlink] = useState<any>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const handleCreate = () => {
    setFormMode('create');
    setSelectedSmartlink(null);
    setIsFormOpen(true);
    setIsDetailOpen(false);
  };
  const handleEdit = (smartlink: any) => {
    setFormMode('edit');
    setSelectedSmartlink(smartlink);
    setIsFormOpen(true);
    setIsDetailOpen(false);
  };
  const handleClone = (smartlink: any) => {
    // Clone the smartlink with a new ID and slightly different name
    const clonedSmartlink = {
      ...smartlink,
      id: `sl_${Date.now()}`,
      name: `${smartlink.name} (Clone)`
    };
    // In a real app, you would save this to the backend
    console.log('Cloned smartlink:', clonedSmartlink);
    alert(`Cloned smartlink: ${clonedSmartlink.name}`);
  };
  const handleArchive = (smartlink: any) => {
    // In a real app, you would update the status in the backend
    console.log('Archived smartlink:', smartlink.id);
    alert(`Archived smartlink: ${smartlink.name}`);
  };
  const handleTestClick = (smartlink: any) => {
    // Open the smartlink URL in a new tab
    window.open(smartlink.baseUrl, '_blank');
  };
  const handleViewDetails = (smartlink: any) => {
    setSelectedSmartlink(smartlink);
    setIsDetailOpen(true);
    setIsFormOpen(false);
  };
  const handleFormSubmit = (formData: any) => {
    // In a real app, you would save this to the backend
    console.log('Form submitted:', formData);
    setIsFormOpen(false);
    alert(`${formMode === 'create' ? 'Created' : 'Updated'} smartlink: ${formData.name}`);
  };
  const handleFormCancel = () => {
    setIsFormOpen(false);
  };
  const handleDetailClose = () => {
    setIsDetailOpen(false);
  };
  const columns = [{
    id: 'status',
    header: 'Status',
    cell: (row: any) => <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${row.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`} data-id="element-13577">
          {row.status === 'active' ? 'Active' : 'Inactive'}
        </span>
  }, {
    id: 'name',
    header: 'Smartlink Name',
    cell: (row: any) => <div className="font-medium text-purple-600 dark:text-purple-400" data-id="element-13578">
          {row.name}
        </div>
  }, {
    id: 'offers',
    header: 'Offers',
    cell: (row: any) => <div data-id="element-13579">{row.offersInPool.length} offers</div>
  }, {
    id: 'strategy',
    header: 'Strategy',
    cell: (row: any) => {
      const strategyMap: Record<string, string> = {
        rpm: 'RPM Optimized',
        epc: 'EPC Optimized',
        cvr: 'CVR Optimized',
        manual: 'Manual (Priority/Weight)'
      };
      return <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${row.strategyId === 'rpm' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' : row.strategyId === 'epc' ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' : row.strategyId === 'cvr' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'}`} data-id="element-13580">
            {strategyMap[row.strategyId] || row.strategyId}
          </span>;
    }
  }, {
    id: 'segments',
    header: 'Segments',
    cell: (row: any) => <div className="flex flex-wrap gap-1" data-id="element-13581">
          {row.segments.map((segment: string) => <span key={segment} className="px-1.5 py-0.5 text-xs bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-400 rounded" data-id="element-13582">
              {segment.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            </span>)}
          {row.segments.length > 2 && <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400 rounded" data-id="element-13583">
              +{row.segments.length - 2} more
            </span>}
        </div>
  }, {
    id: 'geos',
    header: 'GEOs',
    cell: (row: any) => <div className="flex flex-wrap gap-1" data-id="element-13584">
          {row.geos.slice(0, 3).map((geo: string) => <span key={geo} className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded" data-id="element-13585">
              {geo.toUpperCase()}
            </span>)}
          {row.geos.length > 3 && <span className="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400 rounded" data-id="element-13586">
              +{row.geos.length - 3} more
            </span>}
        </div>
  }, {
    id: 'clicks',
    header: 'Clicks',
    cell: (row: any) => <div data-id="element-13587">{row.performance.clicks.toLocaleString()}</div>
  }, {
    id: 'conversions',
    header: 'Conversions',
    cell: (row: any) => <div data-id="element-13588">{row.performance.conversions.toLocaleString()}</div>
  }, {
    id: 'epc',
    header: 'EPC',
    cell: (row: any) => <div data-id="element-13589">${row.performance.epc.toFixed(2)}</div>
  }, {
    id: 'cr',
    header: 'CR',
    cell: (row: any) => <div data-id="element-13590">{(row.performance.cr * 100).toFixed(2)}%</div>
  }, {
    id: 'rpm',
    header: 'RPM',
    cell: (row: any) => <div data-id="element-13591">${row.performance.rpm.toFixed(2)}</div>
  }, {
    id: 'revenue',
    header: 'Revenue',
    cell: (row: any) => <div className="font-medium" data-id="element-13592">
          ${row.performance.revenue.toLocaleString()}
        </div>
  }, {
    id: 'actions',
    header: 'Actions',
    cell: (row: any) => <div className="flex space-x-2" data-id="element-13593">
          <button onClick={e => {
        e.stopPropagation();
        handleEdit(row);
      }} className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300" title="Edit" data-id="element-13594">
            Edit
          </button>
          <button onClick={e => {
        e.stopPropagation();
        handleClone(row);
      }} className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300" title="Clone" data-id="element-13595">
            <Copy size={16} data-id="element-13596" />
          </button>
          <button onClick={e => {
        e.stopPropagation();
        handleArchive(row);
      }} className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300" title="Archive" data-id="element-13597">
            <Archive size={16} data-id="element-13598" />
          </button>
          <button onClick={e => {
        e.stopPropagation();
        handleTestClick(row);
      }} className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300" title="Test Click" data-id="element-13599">
            <ExternalLink size={16} data-id="element-13600" />
          </button>
        </div>,
    sortable: false
  }];
  return <div data-id="element-13601">
      <div className="flex justify-between items-center mb-6" data-id="element-13602">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-13603">
          Smartlinks & Rotators
        </h1>
        <button onClick={handleCreate} className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center" data-id="element-13604">
          <Plus size={16} className="mr-2" data-id="element-13605" />
          Create Smartlink
        </button>
      </div>
      {!isFormOpen && !isDetailOpen && <DataTable data={smartlinks} columns={columns} title="Smartlinks & Rotators" onRowClick={handleViewDetails} data-id="element-13606" />}
      {isFormOpen && <SmartlinkForm mode={formMode} initialData={selectedSmartlink} onSubmit={handleFormSubmit} onCancel={handleFormCancel} data-id="element-13607" />}
      {isDetailOpen && selectedSmartlink && <SmartlinkDetail smartlink={selectedSmartlink} onClose={handleDetailClose} onEdit={() => handleEdit(selectedSmartlink)} onClone={() => handleClone(selectedSmartlink)} onArchive={() => handleArchive(selectedSmartlink)} data-id="element-13608" />}
    </div>;
};